import { useShallow } from 'zustand/react/shallow';

import { EmailSendModeDto, ResourceIdDto } from '@/api/generated';
import { NewEmailAttachmentModel } from '@/components/emailWindow/components/attachments/model/NewEmailAttachment.model.ts';
import { useNewEmailStore } from '@/views/emails/context/NewEmails.store.ts';
import {
  EditedEmailState,
  NEW_EMAIL_VIEW_MODE,
  NewEmailContextProps,
} from '@/views/emails/context/NewEmails.types.ts';

const EMPTY_ATTACHMENTS: NewEmailAttachmentModel[] = [];

type NewEmailDraftControllerState =
  | {
      emailId: string;
      onEmailId: string | undefined;
      sendMode: EmailSendModeDto;
      state: EditedEmailState.READY;
      threadId: string;
    }
  | {
      state: EditedEmailState.IN_PROGRESS;
    }
  | null;

type NewEmailDraftInitializerState = {
  draftRequestId: string;
  emailId: string | undefined;
  mode: EmailSendModeDto;
  onEmailId: string | undefined;
  resource: ResourceIdDto | undefined;
  sourceEmailIsDraft: boolean | undefined;
  state: EditedEmailState;
} | null;

type NewEmailReadyViewState = {
  emailId: string;
  isCollapsed: boolean;
  windowMode: NEW_EMAIL_VIEW_MODE | undefined;
} | null;

export const useNewEmailAttachments = (): NewEmailAttachmentModel[] => {
  return useNewEmailStore(state => state.newEmailState?.attachments ?? EMPTY_ATTACHMENTS);
};

export const useNewEmailAutosavePaused = (): boolean => {
  return useNewEmailStore(state => state.autosavePaused);
};

export const useNewEmailEditedState = (): EditedEmailState | undefined => {
  return useNewEmailStore(state => state.newEmailState?.state);
};

export const useNewEmailDraftControllerState = (): NewEmailDraftControllerState => {
  return useNewEmailStore(
    useShallow(state => {
      const newEmailState = state.newEmailState;

      if (!newEmailState) {
        return null;
      }

      if (newEmailState.state === EditedEmailState.IN_PROGRESS) {
        return { state: newEmailState.state };
      }

      return {
        emailId: newEmailState.emailId,
        onEmailId: newEmailState.email?.emailId,
        sendMode: newEmailState.mode,
        state: newEmailState.state,
        threadId: newEmailState.threadId,
      };
    }),
  );
};

export const useNewEmailDraftInitializerState = (): NewEmailDraftInitializerState => {
  return useNewEmailStore(
    useShallow(state => {
      const newEmailState = state.newEmailState;

      if (!newEmailState) {
        return null;
      }

      return {
        draftRequestId: newEmailState.draftRequestId,
        emailId: newEmailState.state === EditedEmailState.READY ? newEmailState.emailId : undefined,
        mode: newEmailState.mode,
        onEmailId: newEmailState.email?.emailId,
        resource: newEmailState.resource,
        sourceEmailIsDraft: newEmailState.email?.isDraft,
        state: newEmailState.state,
      };
    }),
  );
};

export const useNewEmailIsAnyAttachmentNotReady = (): boolean => {
  return useNewEmailStore(
    state => state.newEmailState?.attachments.some(item => !item.isReady) ?? false,
  );
};

export const useNewEmailIsFloatingWindow = (): boolean => {
  return useNewEmailStore(
    state =>
      Boolean(state.newEmailState?.mode) &&
      state.newEmailState?.windowMode === NEW_EMAIL_VIEW_MODE.FLOATING_WINDOW,
  );
};

export const useNewEmailIsNewModeWithoutSourceEmail = (): boolean => {
  return useNewEmailStore(
    state => !state.newEmailState?.email && state.newEmailState?.mode === EmailSendModeDto.New,
  );
};

export const useNewEmailReadyEmailId = (): string | undefined => {
  return useNewEmailStore(state =>
    state.newEmailState?.state === EditedEmailState.READY ? state.newEmailState.emailId : undefined,
  );
};

export const useNewEmailReadyViewState = (): NewEmailReadyViewState => {
  return useNewEmailStore(
    useShallow(state => {
      const newEmailState = state.newEmailState;

      if (newEmailState?.state !== EditedEmailState.READY) {
        return null;
      }

      return {
        emailId: newEmailState.emailId,
        isCollapsed: newEmailState.isCollapsed ?? false,
        windowMode: newEmailState.windowMode,
      };
    }),
  );
};

export const useNewEmailSetAutosavePaused = (): NewEmailContextProps['setAutosavePaused'] => {
  return useNewEmailStore(state => state.setAutosavePaused);
};

export const useNewEmailUpdateState = (): NewEmailContextProps['updateNewEmailState'] => {
  return useNewEmailStore(state => state.updateNewEmailState);
};

export const useNewEmailWindowMode = (): NEW_EMAIL_VIEW_MODE | undefined => {
  return useNewEmailStore(state => state.newEmailState?.windowMode);
};
